<template>
    <div id="comparisonBar" v-if="comparedCars.length > 0" class="space-bottom-32px-lg space-bottom-lg">
        <div class="d-sm-flex justify-content-between justify-content-lg-start align-items-center mb-3">
            <h3 class="h4 mb-2 mb-sm-0">
                {{ props.headerLabel }}
            </h3>
            <div class="d-flex align-items-center">
                <a :href="props.compareUrl" class="btn btn-primary btn-sm px-3 ms-sm-3 d-flex align-items-center">
                    {{ props.compareButtonLabel }}
                    <img src="/images/mg/icons/ico-chevron-right-white.svg" alt="" class="icon icon--w16 ms-2">
                </a>
                <a href="#" class="link link-primary px-3 d-flex align-items-center" @click.prevent="clearComparedCars">
                    <span class="text--grey text--semi-small text--500">{{ props.resetButtonLabel }}</span>
                    <img src="/images/mg/icons/ico-close-sm.svg" alt="" class="icon icon--w12 ms-2" style="top: 1px">
                </a>
            </div>
        </div>

        <div class="comparation-compact">
            <button
                class="carousel-nav carousel-nav-prev"
                @click="scrollLeft"
                :disabled="currentSlideIndex <= 0"
            >
                <img src="/images/mg/icons/ico-chevron-left.svg" alt="" class="icon icon--w10">
            </button>

            <div
                class="carousel-container"
                :class="{ 'dragging': isDragging }"
                ref="carouselContainer"
                @touchstart="handleTouchStart"
                @touchmove="handleTouchMove"
                @touchend="handleTouchEnd"
                @mousedown="handleMouseDown"
                @mousemove="handleMouseMove"
                @mouseup="handleMouseUp"
                @mouseleave="handleMouseUp"
            >
                <div class="carousel-track" :style="{ transform: `translateX(${-scrollPosition}px)` }">
<!--                <div class="carousel-track">-->
                    <!-- Compared cars -->
                    <div v-for="car in displayedCars" :key="car.id" class="carousel-item">
                        <div class="comparation-compact__item">
                            <a href="#" @click.prevent="handleCarClick">
                                <img v-if="car.image_url" :src="car.image_url" class="img-fluid" :title="car.name" :alt="car.name">
                                <img v-else class="img-fluid" :title="car.name" :alt="car.name">
                            </a>
                            <a href="#" @click.prevent="removeFromComparison(car.id)" class="comparation-compact__remove" :title="props.removeLabel">
                                <img src="/images/icons/ico-close-white.svg" alt="" class="icon icon--close">
                            </a>
                        </div>
                    </div>

                    <!-- Empty slots -->
                    <div v-for="i in emptySlots" :key="`empty-${i}`" class="carousel-item">
                        <div class="comparation-compact__item comparation-compact__item--empty"></div>
                    </div>
                </div>
            </div>

            <button
                class="carousel-nav carousel-nav-next"
                @click="scrollRight"
                :disabled="currentSlideIndex >= totalSlides - 1"
            >
                <img src="/images/mg/icons/ico-chevron-left.svg" alt="" class="icon icon--w10" style="transform: rotate(180deg)">
            </button>
        </div>

        <!--    &lt;!&ndash; Pagination indicators &ndash;&gt;-->
        <!--    <div v-if="totalSlides > 1" class="carousel-pagination">-->
        <!--      <span-->
        <!--        v-for="index in totalSlides"-->
        <!--        :key="index"-->
        <!--        class="pagination-dot"-->
        <!--        :class="{ 'active': currentSlideIndex === index - 1 }"-->
        <!--        @click="handlePaginationClick(index - 1)"-->
        <!--      ></span>-->
        <!--    </div>-->

        <!--    &lt;!&ndash; Debug controls (only visible in development) &ndash;&gt;-->
        <!--    <div v-if="isDevelopment" class="debug-controls mt-3">-->
        <!--      <div class="d-flex justify-content-center">-->
        <!--        <button @click="goToSlide(0)" class="btn btn-sm btn-outline-primary mx-1">Slide 1</button>-->
        <!--        <button @click="goToSlide(1)" class="btn btn-sm btn-outline-primary mx-1">Slide 2</button>-->
        <!--        <button @click="forceReflow" class="btn btn-sm btn-outline-secondary mx-1">Force Reflow</button>-->
        <!--      </div>-->
        <!--    </div>-->
    </div>
</template>

<script setup>
import { onMounted, onUnmounted } from 'vue'
import { useCarComparisonBar } from '@/composables/useCarComparisonBar'

const props = defineProps({
    comparisonList: { type: Array, required: true },
    headerLabel: { type: String, required: true },
    compareButtonLabel: { type: String, required: true },
    resetButtonLabel: { type: String, required: true },
    removeLabel: { type: String, required: true },
    compareUrl: { type: String, required: true },
    maxComparedCars: { type: Number, default: 10 } // Maximum number of cars that can be compared
})

const emit = defineEmits(['remove-from-comparison', 'clear-comparison'])

// Use the composable for shared logic
const {
    // Refs
    isDevelopment,
    comparedCars,
    carouselContainer,
    scrollPosition,
    scrollStep,
    touchStartX,
    touchEndX,
    mouseStartX,
    mouseEndX,
    isDragging,
    dragStartPosition,
    isMouseDown,
    justSwiped,

    // Computed
    maxScrollPosition,
    totalSlides,
    currentSlideIndex,
    displayedCars,
    emptySlots,

    // Methods
    fetchComparedCars,
    removeFromComparison,
    clearComparedCars,
    handleCarClick,
    updateScrollStep,
    goToSlide,
    scrollLeft,
    scrollRight,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleDocumentMouseUp,
    initializeCarousel,
    handleResize,
    handleKeyDown,
    handlePaginationClick,
    forceReflow,
    initializeComparisonBar,
    cleanupComparisonBar
} = useCarComparisonBar(props, emit)

// Initialize on mount
onMounted(async () => {
    await initializeComparisonBar()
})

// Clean up event listeners
onUnmounted(() => {
    cleanupComparisonBar()
})
</script>

<style scoped>
.comparation-compact {
    display: flex;
    align-items: center;
    position: relative;
    width: 100%;
}

.carousel-container {
    flex: 1;
    overflow: hidden;
    position: relative;
    width: 100%; /* Ensure the container takes up the full width */
    box-sizing: border-box; /* Include padding in width calculation */
    margin: 0 auto; /* Center the container */
    cursor: grab; /* Show grab cursor to indicate it's draggable */
}

.carousel-container.dragging {
    cursor: grabbing; /* Show grabbing cursor when actively dragging */
    user-select: none; /* Prevent text selection during drag */
}

.carousel-track {
    display: flex;
    flex-wrap: nowrap; /* Prevent items from wrapping to the next line */
    transition: transform 0.3s cubic-bezier(0.2, 0.0, 0.2, 1); /* Smoother, more responsive transition */
    width: max-content; /* Ensure the track is wide enough for all items */
    min-width: 100%; /* Ensure the track is at least as wide as the container */
    justify-content: flex-start; /* Align items to the start of the track */
    align-items: stretch; /* Stretch items to fill the height of the track */
    box-sizing: border-box; /* Include padding in width calculation */
    will-change: transform; /* Optimize for animations */
    transform: translateX(0); /* Ensure initial transform is set */
    touch-action: pan-y; /* Allow vertical scrolling but capture horizontal */
    cursor: inherit; /* Inherit cursor from parent */
}

.dragging .carousel-track {
    transition: none; /* Remove transition during dragging for immediate response */
}

.carousel-item {
    flex: 0 0 205px; /* Fixed width for desktop */
    width: 205px;
    padding: 0;
    margin: 0 4px;
    box-sizing: border-box;
    flex-shrink: 0; /* Prevent items from shrinking */
    flex-grow: 0; /* Prevent items from growing */
    cursor: inherit; /* Inherit cursor from parent */
}

.comparation-compact__item {
    position: relative;
    overflow: hidden;
    background-color: #f8f9fa;
    height: 132px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%; /* Ensure the item takes up the full width of its parent */
    box-sizing: border-box; /* Include padding in width calculation */
    cursor: inherit; /* Inherit cursor from parent */
}

.comparation-compact__item img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    display: block; /* Prevent inline spacing issues */
    margin: 0 auto; /* Center the image horizontally */
    cursor: inherit; /* Inherit cursor from parent */
}

.comparation-compact__item--empty {
    background-color: transparent;
    border: 2px dashed #f8f8f8;
    width: 100%; /* Ensure the empty item takes up the full width of its parent */
    box-sizing: border-box; /* Include border in width calculation */
    cursor: inherit; /* Inherit cursor from parent */
}

.carousel-nav {
    background: #F8F8F8;
    height: 132px;
    margin-top: 0;
    position: static;
    width: 60px;
    border: none;
    display: flex;
    justify-content: center;
    align-items: center;
}

.carousel-nav:hover:not(:disabled) {
    background-color: #f8f9fa;
}

.carousel-nav:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.carousel-nav-prev {
    border-bottom-left-radius: 12px;
    border-top-left-radius: 12px;
    margin-right: 8px;
}

.carousel-nav-next {
    border-bottom-right-radius: 12px;
    border-top-right-radius: 12px;
    margin-left: 8px;
}

/* Pagination styles */
.carousel-pagination {
    display: flex;
    justify-content: center;
    margin-top: 15px;
}

.pagination-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: #dee2e6;
    margin: 0 5px;
    cursor: pointer;
    transition: background-color 0.3s ease;
}

.pagination-dot.active {
    background-color: #007bff;
}

/* Mobile styles */
@media (max-width: 768px) {
    .carousel-item {
        flex: 0 0 150px; /* Fixed width for mobile */
        width: 150px;
        flex-shrink: 0; /* Prevent items from shrinking */
        flex-grow: 0; /* Prevent items from growing */
    }

    .comparation-compact__item {
        height: 100px;
    }

    .carousel-nav {
        display: none; /* Hide navigation arrows on mobile */
    }
}
</style>
